<template>
  <editor-menu-bar v-slot="{ commands, isActive, focused }" :editor="editor">
    <div
      :class="[
        hiddenMenu ? 'menubar is-hidden' : 'menubar',
        getFocusedSelectors(focused),
      ]"
    >
      <TransitionGroup
        :name="commentChangeEventOnBlur ? 'fade-blur' : 'fade-update'"
      >
        <template v-if="menuBar">
          <asc-autocomplete-selector
            key="dropdown"
            class="rich-text-select"
            :items="dropdownOptions"
            :value="getCurrentLevel(isActive)"
            use-strings
            no-array
            label="Text Style"
            @input="handleInput(commands, isActive, $event)"
          ></asc-autocomplete-selector>

          <div key="v0" class="vertical-line"></div>
        </template>

        <template v-if="(focused && canEdit) || menuBar">
          <button
            key="bold"
            class="menubar__button"
            :class="{ 'is-active': isActive.bold() }"
            :disabled="menuBar && isActive.heading()"
            @click="commands.bold"
          >
            <v-icon
              v-if="hiddenMenu || !isActive.heading()"
              :size="18"
              color="black"
            >
              format_bold
            </v-icon>
            <v-icon v-else :size="18" color="grey">format_bold</v-icon>
          </button>

          <button
            key="italic"
            class="menubar__button"
            :class="{ 'is-active': isActive.italic() }"
            :disabled="menuBar && isActive.heading()"
            @click="commands.italic"
          >
            <v-icon
              v-if="hiddenMenu || !isActive.heading()"
              :size="18"
              color="black"
            >
              format_italic
            </v-icon>
            <v-icon v-else :size="18" color="grey">format_italic</v-icon>
          </button>

          <button
            key="underline"
            class="menubar__button"
            :class="{ 'is-active': isActive.underline() }"
            :disabled="menuBar && isActive.heading()"
            @click="commands.underline"
          >
            <v-icon
              v-if="hiddenMenu || !isActive.heading()"
              color="black"
              :size="18"
            >
              format_underline
            </v-icon>
            <v-icon v-else :size="18" color="grey">format_underline</v-icon>
          </button>

          <div key="v1" class="vertical-line"></div>

          <button
            key="bullet_list"
            class="menubar__button"
            :class="{ 'is-active': isActive.bullet_list() }"
            :disabled="menuBar && isActive.heading()"
            @click="commands.bullet_list"
          >
            <v-icon
              v-if="hiddenMenu || !isActive.heading()"
              color="black"
              :size="18"
            >
              format_list_bulleted
            </v-icon>
            <v-icon v-else :size="18" color="grey">
              format_list_bulleted
            </v-icon>
          </button>

          <button
            key="ordered_list"
            class="menubar__button"
            :class="{ 'is-active': isActive.ordered_list() }"
            :disabled="menuBar && isActive.heading()"
            @click="commands.ordered_list"
          >
            <v-icon
              v-if="hiddenMenu || !isActive.heading()"
              color="black"
              :size="18"
            >
              format_list_numbered
            </v-icon>
            <v-icon v-else :size="18" color="grey">
              format_list_numbered
            </v-icon>
          </button>

          <div key="v2" class="vertical-line"></div>

          <button
            key="linkDialog"
            class="menubar__button"
            :class="{
              'is-active': isActive.link(),
              disabled: isLinkDisabled,
            }"
            :disabled="isLinkDisabled"
            @click="displayLinkDialog"
          >
            <asc-tooltip
              v-if="isLinkDisabled"
              tooltip="Select text to add a link"
              top
            >
              <v-icon color="gray" :size="18">link</v-icon>
            </asc-tooltip>
            <v-icon v-else color="black" :size="18">link</v-icon>
          </button>

          <button
            v-if="isActive.link()"
            key="removeLink"
            class="menubar__button"
            @click="removeLink"
          >
            <v-icon color="black" :size="18">link_off</v-icon>
          </button>

          <div v-if="allowImages" key="images" class="vertical-line"></div>

          <button
            v-if="allowImages"
            key="imageDialog"
            class="menubar__button"
            @click="displayImageDialog"
          >
            <asc-tooltip
              :tooltip="getInsertImageTooltip"
              top
              :tooltip-length="0"
            >
              <v-icon color="black" :size="18">image_outline</v-icon>
            </asc-tooltip>
          </button>
        </template>
      </TransitionGroup>
    </div>
  </editor-menu-bar>
</template>

<script>
import { EditorMenuBar } from 'tiptap'
import AscAutocompleteSelector from '../molecules/AscAutocompleteSelector.vue'
import AscTooltip from './AscTooltip'

export default {
  name: 'AscRichTextMenuBar',

  components: {
    EditorMenuBar,
    AscAutocompleteSelector,
    AscTooltip,
  },

  props: {
    editor: {
      type: Object,
      default: () => {},
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
    displayLinkDialog: {
      type: Function,
      default: () => {},
    },
    displayImageDialog: {
      type: Function,
      default: () => {},
    },
    removeLink: {
      type: Function,
      default: () => {},
    },
    hiddenMenu: {
      type: Boolean,
      default: false,
    },
    menuBar: {
      type: Boolean,
      default: false,
    },
    allowImages: {
      type: Boolean,
      default: false,
    },
    allowedImageTypes: {
      type: Array,
      default: () => ['.png', '.jpg', '.jpeg'],
    },
    commentChangeEventOnBlur: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      dropdownOptions: [
        {
          value: 1,
          text: 'Paragraph',
        },
        {
          value: 2,
          text: 'Heading 1',
        },
        {
          value: 3,
          text: 'Heading 2',
        },
        {
          value: 4,
          text: 'Heading 3',
        },
      ],
    }
  },

  computed: {
    getInsertImageTooltip() {
      return `Insert Image (${this.allowedImageTypes.join(', ')})`
    },
    selectedText() {
      const { selection, state } = this.editor
      const { from, to } = selection
      const text = state.doc.textBetween(from, to, ' ')
      return text
    },
    isLinkDisabled() {
      const selected = this.selectedText.trim()
      return !selected && !this.editor.isActive.link()
    },
  },

  methods: {
    getFocusedSelectors(focused) {
      if (this.hiddenMenu) {
        this.$emit('isFocused', focused)
        return this.canEdit && focused ? 'is-focused' : 'not-focused'
      }
    },
    getCurrentLevel(isActive) {
      if (isActive.heading()) {
        if (isActive.heading({ level: 1 })) {
          return 'Heading 1'
        } else if (isActive.heading({ level: 2 })) {
          return 'Heading 2'
        } else if (isActive.heading({ level: 3 })) {
          return 'Heading 3'
        }
      } else {
        return 'Paragraph'
      }
    },
    handleInput(commands, isActive, event) {
      switch (event) {
        case 'Heading 1':
          if (!isActive.heading({ level: 1 })) {
            this.checkOtherStyles(isActive, commands)
            commands.heading({ level: 1 })
          }
          break
        case 'Heading 2':
          if (!isActive.heading({ level: 2 })) {
            this.checkOtherStyles(isActive, commands)
            commands.heading({ level: 2 })
          }
          break
        case 'Heading 3':
          if (!isActive.heading({ level: 3 })) {
            this.checkOtherStyles(isActive, commands)
            commands.heading({ level: 3 })
          }
          break
        default:
          if (isActive.heading()) {
            commands.heading()
          }
          break
      }
    },
    checkOtherStyles(isActive, commands) {
      if (isActive.bold()) {
        commands.bold()
      }
      if (isActive.italic()) {
        commands.italic()
      }
      if (isActive.underline()) {
        commands.underline()
      }
      if (isActive.bullet_list()) {
        commands.bullet_list()
      }
      if (isActive.ordered_list()) {
        commands.ordered_list()
      }
    },
  },
}
</script>

<style lang="scss">
.menubar {
  & > span {
    display: flex;
    flex-direction: row;
  }

  &.is-focused {
    border-bottom: 1px solid $accent-600;

    & .editor__content {
      & .ProseMirror {
        border-bottom: none;
      }
    }
  }

  &.not-focused {
  }

  .fade-update-enter-active,
  .fade-update-leave-active {
    transition: opacity 0.5s;
  }

  .fade-update-enter,
  .fade-update-leave-to {
    opacity: 0;
  }

  .vertical-line {
    margin: 10px;
    border-left: 1.5px solid $gray-300;
  }
}
</style>
